import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Bolt,
  CampaignOutlined,
  Close,
  DeleteOutline,
  EventBusy,
  PauseCircleOutline,
  PlayCircleOutline,
  Replay,
  Visibility,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { WORKFLOW_STATUS, prettyKey, wa } from './waTheme';
import type { WorkflowMeta } from './WorkflowBuilder';

const fmt = (d?: string) =>
  d
    ? new Date(d).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
      })
    : '—';

const MSG_STATUS_COLOR: Record<string, string> = {
  delivered: '#2e7d32', read: '#1565c0', sent: '#00897b', failed: '#c62828',
  suppressed: '#e65100', pending: '#757575', queued: '#757575', cancelled: '#757575', expired: '#e65100',
};

/** Delivered+read as a share of everything that reached WhatsApp. */
function deliveryRate(s: any) {
  const reached = s.sent + s.delivered + s.read + s.failed;
  return reached ? Math.round(((s.delivered + s.read) / reached) * 100) : 0;
}

const StatPill: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <Box sx={{ px: 1, py: 0.3, borderRadius: 1.5, bgcolor: `${color}14`, display: 'flex', gap: 0.5, alignItems: 'baseline' }}>
    <Typography sx={{ fontWeight: 800, fontSize: 13, color }}>{value}</Typography>
    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{label}</Typography>
  </Box>
);

const WorkflowsPanel: React.FC<{
  workflows: any[];
  loading: boolean;
  meta: WorkflowMeta | null;
  studioId: string;
  canWrite: boolean;
  onChanged: () => void;
  onNew: (kind?: 'campaign' | 'automation') => void;
  notify: (msg: string, sev?: 'success' | 'error') => void;
}> = ({ workflows, loading, meta, studioId, canWrite, onChanged, onNew, notify }) => {
  const [filter, setFilter] = useState<'all' | 'campaign' | 'automation'>('all');
  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const segLabel = (k: string) => meta?.segments.find((s) => s.key === k);
  const shown = workflows.filter((w) => filter === 'all' || w.kind === filter);

  const act = async (w: any, action: 'cancel' | 'pause' | 'resume' | 'run-now' | 'delete') => {
    if (action === 'delete' && !window.confirm(`Delete “${w.name}”? Its delivery history stays in the Delivery log.`)) return;
    if (action === 'cancel' && !window.confirm(`Cancel the scheduled campaign “${w.name}”?`)) return;
    setBusy(`${w._id}:${action}`);
    try {
      if (action === 'delete') await apiService.deleteWorkflow(w._id, studioId);
      else {
        const res: any = await apiService.workflowAction(w._id, action, studioId);
        if (action === 'run-now') {
          const r = res?.data ?? res;
          notify(`Ran now — ${r?.queued ?? 0} queued, ${r?.skipped ?? 0} already messaged`);
        }
      }
      if (action !== 'run-now') {
        notify(
          { cancel: 'Campaign cancelled', pause: 'Automation paused', resume: 'Automation resumed', delete: 'Workflow deleted' }[action] as string,
        );
      }
      onChanged();
    } catch (e: any) {
      notify(e?.message ?? 'Action failed', 'error');
    } finally {
      setBusy(null);
    }
  };

  const openDetail = async (w: any) => {
    setDetail({ workflow: w, recent: null });
    setDetailLoading(true);
    try {
      const res: any = await apiService.getWorkflow(w._id, studioId);
      setDetail(res?.data ?? res);
    } catch (e: any) {
      notify(e?.message ?? 'Could not load details', 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading) {
    return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress sx={{ color: wa.teal }} /></Box>;
  }

  if (!workflows.length) {
    return (
      <Box sx={{ textAlign: 'center', py: 6, px: 2, border: '2px dashed rgba(18,140,126,0.25)', borderRadius: 3, bgcolor: '#fff' }}>
        <Box sx={{ width: 76, height: 76, borderRadius: '50%', mx: 'auto', mb: 2, display: 'grid', placeItems: 'center', bgcolor: wa.tint }}>
          <Bolt sx={{ fontSize: 38, color: wa.teal }} />
        </Box>
        <Typography variant="h6" fontWeight={800}>No workflows yet</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460, mx: 'auto', mb: 3 }}>
          Send a one-off WhatsApp campaign, or set up an automation that reminds members about expiring packs and
          tomorrow’s classes — every day, by itself.
        </Typography>
        {canWrite && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
            <Button variant="contained" startIcon={<CampaignOutlined />} onClick={() => onNew('campaign')} sx={{ background: wa.cta, boxShadow: wa.ctaShadow, fontWeight: 800, borderRadius: 2 }}>
              New campaign
            </Button>
            <Button variant="outlined" startIcon={<Bolt />} onClick={() => onNew('automation')} sx={{ borderColor: wa.teal, color: wa.teal, fontWeight: 800, borderRadius: 2 }}>
              New automation
            </Button>
          </Stack>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" mb={2} spacing={1}>
        <ToggleButtonGroup
          size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)}
          sx={{ '& .Mui-selected': { bgcolor: `${wa.tint} !important`, color: `${wa.dark} !important`, fontWeight: 800 }, '& .MuiToggleButton-root': { textTransform: 'none', px: 1.8 } }}
        >
          <ToggleButton value="all">All · {workflows.length}</ToggleButton>
          <ToggleButton value="campaign">Campaigns · {workflows.filter((w) => w.kind === 'campaign').length}</ToggleButton>
          <ToggleButton value="automation">Automations · {workflows.filter((w) => w.kind === 'automation').length}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Stack spacing={1.5}>
        {shown.map((w) => {
          const st = WORKFLOW_STATUS[w.status] ?? WORKFLOW_STATUS.cancelled;
          const seg = segLabel(w.segment);
          const s = w.stats ?? { total: 0, sent: 0, delivered: 0, read: 0, failed: 0, blocked: 0, pending: 0 };
          const rate = deliveryRate(s);
          const isAuto = w.kind === 'automation';
          const when = isAuto
            ? w.status === 'active' ? `Next run ${fmt(w.nextRunAt)}` : `Every day at ${w.automation?.timeIST ?? '—'} IST`
            : w.status === 'scheduled' ? `Sends ${fmt(w.scheduledAt)}` : w.sentAt ? `Sent ${fmt(w.sentAt)}` : `Created ${fmt(w.createdAt)}`;
          return (
            <Box
              key={w._id}
              sx={{
                p: 2, borderRadius: 3, bgcolor: '#fff', border: '1px solid rgba(0,0,0,0.06)',
                display: 'flex', gap: 2, alignItems: { xs: 'flex-start', md: 'center' }, flexDirection: { xs: 'column', md: 'row' },
                transition: 'all .2s', '&:hover': { borderColor: wa.tintBorder, boxShadow: '0 6px 24px rgba(18,140,126,0.08)' },
              }}
            >
              <Stack direction="row" spacing={1.8} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                <Avatar sx={{ width: 46, height: 46, bgcolor: isAuto ? 'rgba(37,211,102,0.14)' : 'rgba(18,140,126,0.1)', color: wa.teal }}>
                  {isAuto ? <Bolt /> : <CampaignOutlined />}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography fontWeight={800} noWrap sx={{ maxWidth: 320 }}>{w.name}</Typography>
                    <Chip size="small" label={st.label} sx={{ height: 20, fontSize: 11, fontWeight: 700, color: st.color, bgcolor: st.bg }} />
                    {w.lastError && (
                      <Tooltip title={w.lastError}><Chip size="small" label="Error" sx={{ height: 20, fontSize: 11, color: '#b71c1c', bgcolor: 'rgba(244,67,54,0.1)' }} /></Tooltip>
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {seg?.icon} {seg?.label ?? w.segment} · {prettyKey(w.templateKey)} · {when}
                  </Typography>
                  <Stack direction="row" spacing={0.8} mt={0.8} flexWrap="wrap" useFlexGap>
                    <StatPill label="sent" value={s.sent + s.delivered + s.read} color="#00897b" />
                    <StatPill label="delivered" value={s.delivered + s.read} color="#2e7d32" />
                    <StatPill label="read" value={s.read} color="#1565c0" />
                    {s.failed > 0 && <StatPill label="failed" value={s.failed} color="#c62828" />}
                    {s.blocked > 0 && <StatPill label="not sent" value={s.blocked} color="#e65100" />}
                    {s.pending > 0 && <StatPill label="queued" value={s.pending} color="#757575" />}
                  </Stack>
                </Box>
              </Stack>

              <Box sx={{ width: { xs: '100%', md: 150 } }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">Delivered</Typography>
                  <Typography variant="caption" fontWeight={800}>{rate}%</Typography>
                </Stack>
                <LinearProgress
                  variant="determinate" value={rate}
                  sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(0,0,0,0.06)', '& .MuiLinearProgress-bar': { bgcolor: rate >= 80 ? wa.green : rate > 0 ? '#ff9800' : '#bdbdbd' } }}
                />
              </Box>

              <Stack direction="row" spacing={0.3}>
                <Tooltip title="Details"><IconButton size="small" onClick={() => openDetail(w)}><Visibility fontSize="small" /></IconButton></Tooltip>
                {canWrite && isAuto && w.status === 'active' && (
                  <>
                    <Tooltip title="Run now"><IconButton size="small" disabled={!!busy} onClick={() => act(w, 'run-now')}><Replay fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Pause"><IconButton size="small" disabled={!!busy} onClick={() => act(w, 'pause')}><PauseCircleOutline fontSize="small" /></IconButton></Tooltip>
                  </>
                )}
                {canWrite && isAuto && w.status === 'paused' && (
                  <Tooltip title="Resume"><IconButton size="small" disabled={!!busy} onClick={() => act(w, 'resume')} sx={{ color: wa.teal }}><PlayCircleOutline fontSize="small" /></IconButton></Tooltip>
                )}
                {canWrite && w.status === 'scheduled' && (
                  <Tooltip title="Cancel schedule"><IconButton size="small" disabled={!!busy} onClick={() => act(w, 'cancel')}><EventBusy fontSize="small" /></IconButton></Tooltip>
                )}
                {canWrite && w.status !== 'processing' && (
                  <Tooltip title="Delete"><IconButton size="small" disabled={!!busy} onClick={() => act(w, 'delete')} sx={{ color: '#c62828' }}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
                )}
              </Stack>
            </Box>
          );
        })}
      </Stack>

      {/* Detail */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        {detail && (
          <>
            <Box sx={{ background: wa.gradient, color: '#fff', px: 3, py: 2.2 }}>
              <Stack direction="row" alignItems="center">
                <Box flexGrow={1}>
                  <Typography variant="h6" fontWeight={800}>{detail.workflow.name}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {detail.workflow.kind === 'automation' ? `Automation · every day at ${detail.workflow.automation?.timeIST} IST` : 'Campaign'} ·{' '}
                    {prettyKey(detail.workflow.templateKey)} · {segLabel(detail.workflow.segment)?.label}
                  </Typography>
                </Box>
                <IconButton onClick={() => setDetail(null)} sx={{ color: '#fff' }}><Close /></IconButton>
              </Stack>
            </Box>
            <DialogContent sx={{ bgcolor: '#fafafa' }}>
              {(() => {
                const s = detail.workflow.stats ?? {};
                const tiles = [
                  ['Sent', (s.sent ?? 0) + (s.delivered ?? 0) + (s.read ?? 0), '#00897b'],
                  ['Delivered', (s.delivered ?? 0) + (s.read ?? 0), '#2e7d32'],
                  ['Read', s.read ?? 0, '#1565c0'],
                  ['Failed', s.failed ?? 0, '#c62828'],
                  ['Not sent', s.blocked ?? 0, '#e65100'],
                ] as const;
                return (
                  <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    {tiles.map(([label, value, color]) => (
                      <Grid key={label} size={{ xs: 6, sm: 'grow' }}>
                        <Box sx={{ p: 1.6, borderRadius: 2.5, bgcolor: '#fff', border: `1px solid ${color}22` }}>
                          <Typography sx={{ fontSize: 24, fontWeight: 800, color }}>{value}</Typography>
                          <Typography variant="caption" color="text.secondary">{label}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                );
              })()}
              {detail.workflow.kind === 'automation' && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Ran {detail.workflow.runCount ?? 0} times · last run {fmt(detail.workflow.lastRunAt)} matched{' '}
                  {detail.workflow.totals?.lastRunMatched ?? 0}, sent {detail.workflow.totals?.lastRunQueued ?? 0} new
                  {detail.workflow.status === 'active' ? ` · next run ${fmt(detail.workflow.nextRunAt)}` : ''}
                </Typography>
              )}
              <Typography fontWeight={800} mb={1}>Recent recipients</Typography>
              {detailLoading || !detail.recent ? (
                <CircularProgress size={22} sx={{ color: wa.teal }} />
              ) : detail.recent.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Nothing sent yet.</Typography>
              ) : (
                <Box sx={{ overflowX: 'auto', bgcolor: '#fff', borderRadius: 2, border: '1px solid rgba(0,0,0,0.06)' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>When</TableCell><TableCell>Member</TableCell><TableCell>Status</TableCell><TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.recent.map((m: any) => (
                        <TableRow key={m._id}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(m.createdAt)}</TableCell>
                          <TableCell>{m.recipient?.id?.name ?? '—'}</TableCell>
                          <TableCell>
                            <Chip size="small" label={m.status} sx={{ height: 20, fontSize: 11, fontWeight: 700, color: MSG_STATUS_COLOR[m.status] ?? '#555', bgcolor: `${MSG_STATUS_COLOR[m.status] ?? '#555'}14` }} />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 320 }}>{m.failure?.humanReason ?? '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default WorkflowsPanel;
